import {useEffect,useState} from 'react';
import type {SmtPrintJobViewModel,SmtPrintingViewModel} from '../../../../packages/runtime/smt-printing-contract.ts';
import type {SmtPrintingPort} from '../smt-printing-port.ts';

const semanticLabels:Readonly<Record<SmtPrintJobViewModel['semanticState'],string>>={
  awaiting_dispatch:'待派送',
  native_execution_in_progress:'正在交畀打印裝置',
  native_execution_accepted:'裝置已接受 · 未確認出紙',
  execution_failed:'執行失敗',
  execution_uncertain:'結果不確定 · 先檢查有冇出紙',
};

const roleLabels:Readonly<Record<string,string>>={
  receipt:'收據',
  kitchen:'廚房',
  label:'標籤',
};

type ActionDraft={kind:'retry'|'reprint';intentId:string;reason:string};

export function OrderPrintingPanel({runtime,orderId}:{runtime:Pick<SmtPrintingPort,'readOrderPrinting'|'reprintOrderPrint'|'retryFailedOrderPrint'>;orderId:string}){
  const [snapshot,setSnapshot]=useState<SmtPrintingViewModel|null>(null);
  const [loading,setLoading]=useState(true);
  const [error,setError]=useState<string|null>(null);
  const [message,setMessage]=useState<string|null>(null);
  const [draft,setDraft]=useState<ActionDraft|null>(null);

  useEffect(()=>{
    let ignore=false;
    setLoading(true);
    setSnapshot(null);
    setError(null);
    setMessage(null);
    setDraft(null);
    runtime.readOrderPrinting(orderId)
      .then(next=>{if(!ignore)setSnapshot(next);})
      .catch(reason=>{if(!ignore)setError(reason instanceof Error?reason.message:'SMT_PRINTING_READ_FAILED');})
      .finally(()=>{if(!ignore)setLoading(false);});
    return()=>{ignore=true;};
  },[orderId,runtime]);

  const runAction=async()=>{
    if(!draft)return;
    const reason=draft.reason.trim();
    if(!reason){setMessage('請填寫操作原因');return;}
    setLoading(true);
    setMessage(null);
    try{
      const result=draft.kind==='retry'
        ?await runtime.retryFailedOrderPrint(orderId,draft.intentId,reason)
        :await runtime.reprintOrderPrint(orderId,draft.intentId,reason);
      setSnapshot(result.snapshot);
      if(result.kind==='rejected')setMessage(result.code);
      else{setMessage('操作已由 C Print Authority 接受');setDraft(null);}
      setError(null);
    }catch(reasonValue){
      setError(reasonValue instanceof Error?reasonValue.message:'SMT_PRINTING_MUTATION_FAILED');
    }finally{
      setLoading(false);
    }
  };

  return <section className="order-printing" aria-label="打印執行" aria-busy={loading}>
    <header className="order-printing-head">
      <div><p className="eyebrow">C · Print Authority</p><h3>打印執行</h3></div>
      <span>實體出紙需 A / Hardware 證據</span>
    </header>

    {error?<div className="order-printing-alert" role="alert">{error}</div>:null}
    {loading&&!snapshot?<div className="order-printing-empty">讀取打印狀態中…</div>:null}
    {!loading&&snapshot?.jobs.length===0?<div className="order-printing-empty">此訂單未有打印工作</div>:null}

    <div className="order-printing-jobs">
      {snapshot?.jobs.map(job=><article className={`order-printing-job state-${job.status}`} key={job.intentId}>
        <div className="order-printing-job-head">
          <div><strong>{job.documentKind}</strong><small>{job.intentId}</small></div>
          <span>{semanticLabels[job.semanticState]}</span>
        </div>
        <div className="order-printing-proof"><b>未有實體出紙確認</b><small>C狀態：{job.status} · Attempts {job.attempts}</small></div>
        {job.requiresManualAttention?<p className="order-printing-warning">⚠ 狀態不確定：可能已出紙。先人工檢查，禁止盲目重試。</p>:null}
        <div className="order-printing-targets">
          {job.targets.map((target,index)=><div key={`${job.intentId}:${target.role}:${index}`}><span>{roleLabels[target.role]??target.role} ×{target.copies}</span><b>{semanticLabels[target.semanticState]}</b>{target.lastError?<small>{target.lastError}</small>:null}</div>)}
        </div>
        <div className="order-printing-actions">
          {job.canRetryFailed?<button type="button" aria-label={`重試 ${job.intentId}`} disabled={loading} onClick={()=>setDraft({kind:'retry',intentId:job.intentId,reason:''})}>重試失敗項目</button>:null}
          {job.canExplicitReprint?<button type="button" aria-label={`補印 ${job.intentId}`} disabled={loading} onClick={()=>setDraft({kind:'reprint',intentId:job.intentId,reason:''})}>明確補印</button>:null}
        </div>
      </article>)}
    </div>

    {draft?<div className="order-printing-action-form" role="group" aria-label={draft.kind==='retry'?'確認重試':'確認補印'}>
      <div><b>{draft.kind==='retry'?'重試失敗項目':'明確補印'}</b><small>{draft.intentId}</small></div>
      <label><span>操作原因</span><input aria-label="操作原因" value={draft.reason} onChange={event=>setDraft({...draft,reason:event.target.value})} placeholder="必須記錄人工判斷／補印原因"/></label>
      <div className="order-printing-form-actions"><button type="button" onClick={()=>setDraft(null)}>取消</button><button type="button" disabled={loading} onClick={()=>void runAction()}>{draft.kind==='retry'?'確認重試':'確認補印'}</button></div>
    </div>:null}

    {message?<div className="order-printing-message" role="status">{message}</div>:null}
  </section>;
}
